import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput.tsx';
import { DataTable } from 'components/tables/data-table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { useCallback, useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { useUpdateTimecardEntryControlMutation } from 'store/api/tenants.ts';
import { DayOfWeek, TenantConfigurationDto, TimecardEntryControlUpdateDto } from 'types/tenants.ts';
import { t } from 'utils/getTranslations.ts';
import { useHasPermissions } from 'utils/permissionUtil';
import WeekdayPicker from './WeekdayPicker.tsx';

interface Props {
    tenantConfiguration: TenantConfigurationDto;
}

const TimecardEntryControl = ({ tenantConfiguration }: Props): JSX.Element => {
    const [updateTimecardEntryControl] = useUpdateTimecardEntryControlMutation();
    const hasUpdatePermission = useHasPermissions(['UPDATE.BACK_OFFICE_SETTINGS']);
    const selectedValue = tenantConfiguration.timecardEntryDeadlineWithinCurrentMonth;

    const { getValues, control, setValue } = useForm<TimecardEntryControlUpdateDto>({
        defaultValues: {
            isTimecardEntryPreviousMonthBlocked: tenantConfiguration.isTimecardEntryPreviousMonthBlocked,
            timecardEntryDeadlineWithinCurrentMonth: tenantConfiguration.timecardEntryDeadlineWithinCurrentMonth,
            isTimecardEntryPreviousWeekBlocked: tenantConfiguration.isTimecardEntryPreviousWeekBlocked,
            timecardEntryDeadlineWithinCurrentWeek: tenantConfiguration.timecardEntryDeadlineWithinCurrentWeek,
        },
    });
    const selectedWeekday = useWatch({ control, name: 'timecardEntryDeadlineWithinCurrentWeek' }) ?? DayOfWeek.MONDAY;

    // Only one restriction can be on: checking one clears the other, and a restriction left off drops its deadline.
    const saveRestriction = useCallback(
        (restriction: 'month' | 'week', checked: boolean) => {
            const isMonthBlocked = checked && restriction === 'month';
            const isWeekBlocked = checked && restriction === 'week';
            setValue('isTimecardEntryPreviousMonthBlocked', isMonthBlocked);
            setValue('isTimecardEntryPreviousWeekBlocked', isWeekBlocked);
            if (!isMonthBlocked) {
                setValue('timecardEntryDeadlineWithinCurrentMonth', null);
            }
            if (!isWeekBlocked) {
                setValue('timecardEntryDeadlineWithinCurrentWeek', null);
            }
            updateTimecardEntryControl({ ...getValues() });
        },
        [getValues, setValue, updateTimecardEntryControl]
    );

    const columns = useMemo<DataTableColumnDef<TenantConfigurationDto>[]>(
        () => [
            {
                header: t('configuration.restrict-timecard-entry'),
                id: 'isTimecardEntryControlEnabled',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                },
                cell: () => (
                    <CheckboxInput
                        control={control}
                        name="isTimecardEntryPreviousMonthBlocked"
                        disabled={!hasUpdatePermission}
                        onChange={(checked) => saveRestriction('month', checked)}
                    />
                ),
            },
            {
                header: t('configuration.apply-restriction-date'),
                id: 'timecardEntryDeadlineWithinCurrentMonth',
                enableSorting: false,
                meta: {
                    widthWeight: 3,
                },
                cell: ({ row }) =>
                    row.original.isTimecardEntryPreviousMonthBlocked ? (
                        <Controller
                            control={control}
                            name="timecardEntryDeadlineWithinCurrentMonth"
                            render={() => (
                                <Select<number>
                                    value={selectedValue}
                                    disabled={!hasUpdatePermission}
                                    onValueChange={(value) => {
                                        if (value === null) return;
                                        setValue('timecardEntryDeadlineWithinCurrentMonth', value);
                                        updateTimecardEntryControl({ ...getValues() });
                                    }}
                                >
                                    <SelectTrigger
                                        aria-label={t('configuration.apply-restriction-date')}
                                        className="bg-grey-100 [&_svg]:text-primary border-0 pr-1 font-semibold shadow-none"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((value) => (
                                            <SelectItem key={value} value={value}>
                                                {value}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        />
                    ) : null,
            },
        ],
        [control, getValues, selectedValue, setValue, updateTimecardEntryControl, hasUpdatePermission, saveRestriction]
    );

    const weekColumns = useMemo<DataTableColumnDef<TenantConfigurationDto>[]>(
        () => [
            {
                header: t('configuration.restrict-timecard-entry-weeks'),
                id: 'isTimecardEntryPreviousWeekBlocked',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                },
                cell: () => (
                    <CheckboxInput
                        control={control}
                        name="isTimecardEntryPreviousWeekBlocked"
                        disabled={!hasUpdatePermission}
                        onChange={(checked) => saveRestriction('week', checked)}
                    />
                ),
            },
            {
                header: t('configuration.apply-restriction-weekday'),
                id: 'timecardEntryDeadlineWithinCurrentWeek',
                enableSorting: false,
                meta: {
                    widthWeight: 3,
                },
                cell: ({ row }) =>
                    row.original.isTimecardEntryPreviousWeekBlocked ? (
                        <WeekdayPicker
                            label={t('configuration.apply-restriction-weekday')}
                            value={selectedWeekday}
                            disabled={!hasUpdatePermission}
                            onChange={(day) => {
                                setValue('timecardEntryDeadlineWithinCurrentWeek', day);
                                updateTimecardEntryControl({ ...getValues() });
                            }}
                        />
                    ) : null,
            },
        ],
        [
            control,
            getValues,
            selectedWeekday,
            setValue,
            updateTimecardEntryControl,
            hasUpdatePermission,
            saveRestriction,
        ]
    );

    const rows = useMemo(() => [tenantConfiguration], [tenantConfiguration]);

    return (
        <>
            <div className="flex justify-between">
                <h3 className="text-h3 text-balance">
                    <FormattedMessage id="configuration.timecard-entry-control" />
                </h3>
            </div>
            <DataTable<TenantConfigurationDto>
                columns={columns}
                data={rows}
                columnConfigId="configuration-timecard-entry-control"
            />
            <DataTable<TenantConfigurationDto>
                columns={weekColumns}
                data={rows}
                columnConfigId="configuration-timecard-entry-control-weekly"
            />
        </>
    );
};

export default TimecardEntryControl;
